import BottomNavigationScreen from './Navigation/Bottom';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { TabsCustomization } from '../Logistics/Tabs';
import moment from 'moment/moment';
import { toast } from 'material-react-toastify';
import { FilterPopup, Toast as CustomToast } from '../../Components';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginPageStyles } from '../../styles';
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { setCurrentTab } from '../../redux/features/Tab';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CssBaseline,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import {
  setPractices,
  fetchPracticesData,
  fetchEnroutedPractices,
  fetchRoutePractices,
} from '../../redux/features/Practice';
import { destroyUser, setUserRoute } from '../../redux/features/User';
import { useAuth, useLiveFeed } from '../../hooks';

const OPTIONS_INTERSECTION_OBSERVER = {
  root: null,
  rootMargin: '-10% 0px', // set negative rootMargin to start intersection earlier
  threshold: 0.9, // set higher threshold to require more of the element to be visible
};

function Logistics(props) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((state) => state.userReducer.user);
  const [dropDownHeight, setDropDownHeight] = useState(false);
  const classes = loginPageStyles();
  const [searchValue, setSearchValue] = useState('');
  const practices = useSelector((state) => state.practicesReducer.practices);
  const loading = useSelector((state) => state.practicesReducer.loading);
  const hasMore = useSelector((state) => state.practicesReducer.hasMore);
  const totalPages = useSelector((state) => state.practicesReducer.totalPages);
  const [hasRecieved, setHasRecieved] = useLiveFeed();
  const [fetchAgain, setFetchAgain] = useState(false);
  const [filterPopup, setFilterPopup] = useState(false);
  const [apply, setApply] = useState(false);
  const [tabIndex, setTabIndex] = useState(1);
  const [pageNo, setPageNo] = useState(1);
  const [searchedRoutePractices, setSearchedRoutePractices] = useState();


  // Infinite Scroll Logic
  const observer = useRef();
  const lastElementRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          pageNo < totalPages && setPageNo((prev) => prev + 1);
        }
      }, OPTIONS_INTERSECTION_OBSERVER);
      if (node) observer.current.observe(node);
    },

    [loading, hasMore]
  );

  // This state is for handling filters through out the app
  const [queryParams, setQueryParams] = useState({
    userId: user?.id,
    order_type: [],
    attributeToSort: '',
    status: 'assigned',
    sortBy: 'asc',
    sortByDate: false,
    startDate: new Date(),
    endDate: new Date(),
  });

  useAuth(); // Authorization Hook
  useEffect(() => {
    dispatch(setCurrentTab('Logistics'));
  }, []);

  // This function set's our query parameters for making call to server
  const getQueryParams = () => {
    let data = {
      rider_id: user?.id,
      search: searchValue,
      status:
        tabIndex === 1
          ? 'assigned'
          : tabIndex === 2
          ? 'collected'
          : 'delivered',
      page: pageNo,
      route_id: user.rider_route?.id,
    };
    if (queryParams.order_type.length !== 0) {
      queryParams.order_type.forEach((o) => {
        data = { ...data, [o.toLowerCase()]: true };
      });
    }
    if (queryParams.attributeToSort !== '') {
      data = {
        ...data,
        sort: queryParams.attributeToSort,
        direction: queryParams.sortBy.toLowerCase(),
      };
    }

    if (queryParams.sortByDate) {
      if (queryParams.endDate !== null) {
        data = {
          ...data,
          start_date: moment(queryParams.startDate).format('YYYY-MM-DD'),
          end_date: moment(queryParams.endDate).format('YYYY-MM-DD'),
        };
      }
    }

    return data;
  };

  /*
  This function sorts data, it takes 2 params:
  1. attribute to sort
  2. order
  */

  const sortPractices = (by, order) => {
    let temp = JSON.parse(JSON.stringify(practices));
    by === 'name'
      ? temp.sort((a, b) => {
          var x = a.name.toUpperCase();
          var y = b.name.toUpperCase();
          return order === 'asc'
            ? x === y
              ? 0
              : x > y
              ? 1
              : -1
            : x === y
            ? 0
            : x > y
            ? -1
            : 1;
        })
      : temp.sort((a, b) => {
          return order === 'asc'
            ? a.orders_count[by] - b.orders_count[by]
            : b.orders_count[by] - a.orders_count[by];
        });

    dispatch(setPractices([...temp]));
    setQueryParams((prev) => {
      return {
        ...prev,
        attributeToSort: '',
        sortBy: '',
      };
    });
  };

  // useEffect for resetting state when tab Index changes
  useEffect(() => {
    tabIndex === 0
      ? searchValue === '' && dispatch(setPractices([]))
      : queryParams.attributeToSort === '' && dispatch(setPractices([]));
  }, [tabIndex, searchValue, apply, fetchAgain]);

  // UseEffect for fetching practices Data
  useEffect(() => {
    let promise;
    if (tabIndex === 0 && searchValue !== '') {
      setSearchedRoutePractices(
        practices.filter((practice) =>
          practice.name?.toLowerCase().includes(searchValue.toLowerCase())
        )
      );
    }
    else if (tabIndex === 0 && searchValue === '') {
      setSearchedRoutePractices(null);
      promise = dispatch(fetchRoutePractices({ userId: user?.id }));
      promise
        .unwrap()
        .then((response) => {
          dispatch(setUserRoute(response.data.route));
        })
        .catch((err) => {
          if (
            err?.error === 'You need to sign in or sign up before continuing.'
          ) {
            toast(
              <CustomToast
                severity="warning"
                message="Your session has been expired"
              />,
              {
                closeButton: false,
              }
            );
            dispatch(destroyUser());
            navigate('/login');
          }
          if (err?.name === 'AbortError') return;
        });
    } else if (tabIndex !== 0) {
      let params = { ...getQueryParams() };
      if (params.sort !== undefined && params.sort !== '') {
        sortPractices(params.sort, params.direction);
      } else {
        if (queryParams.endDate !== null) {
          promise = dispatch(fetchPracticesData(params));
          promise.unwrap().catch((err) => {
            if (
              err?.error === 'You need to sign in or sign up before continuing.'
            ) {
              toast(
                <CustomToast
                  severity="warning"
                  message="Your session has been expired"
                />,
                {
                  closeButton: false,
                }
              );
              dispatch(destroyUser());
              navigate('/login');
            }
            if (err?.name === 'AbortError') return;
          });
        }
      }
    }

    // Cleanup
    return () => {
      promise && promise.abort();
    };
  }, [searchValue, apply, tabIndex, pageNo, fetchAgain]);

  // This useEffect fetches EnroutedPractices
  useEffect(() => {
    const promise = dispatch(
      fetchEnroutedPractices({
        riderId: user?.id,
        routeId: user.rider_route?.id,
      })
    );
    promise.catch((err) => {
      if (err?.error === 'You need to sign in or sign up before continuing.') {
        toast(
          <CustomToast
            severity="warning"
            message="Your session has been expired"
          />,
          { closeButton: false }
        );
        dispatch(destroyUser());
        navigate('/login');
      } else {
        toast(
          <CustomToast
            severity="warning"
            message="Error While fetching Enrouted Practices"
          />,
          { autoClose: false, closeButton: false }
        );
      }
    });
    return () => {
      promise.abort();
    };
  }, [fetchAgain, tabIndex]);

  // This useEffect is for clearing search value when changing tabs
  useEffect(() => {
    setSearchValue('');
  }, [tabIndex]);

  // This function handles new Order Alert
  const handleNewOrderAlert = () => {
    toast(
      <CustomToast
        severity="info"
        message="New Orders Assigned. Tap to view"
        handleClick={() => setFetchAgain((prev) => !prev)}
      />,
      { closeButton: false, autoClose: false }
    );
    setHasRecieved(false);
  };

  // Event Handlers
  const searchFilter = async (e) => {
    setSearchValue(e.target.value);
    setPageNo(1);
  };
  const handleFilter = () => {
    setFilterPopup(true);
  };

  return (
    <React.Fragment>
      <CssBaseline />
      {/* Displays new order alert */}
      {hasRecieved && handleNewOrderAlert()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100vh',
          width: '100%',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <Box
            className={classes.heading10}
            sx={{
              color: '#0F2853',
              fontWeight: '700',
              fontSize: '21px',
              lineHeight: '27px',
              my: '4px',
              textAlign: 'center',
            }}
          >
            Logistics
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              marginBottom: '12px',
              mx: '15px',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search"
              className={classes.textField}
              sx={{ pl: '8px' }}
              value={searchValue}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#8493AE' }} />
                  </InputAdornment>
                ),
                endAdornment: searchValue.length > 0 && (
                  <InputAdornment position="end">
                    <IconButton
                      sx={{ p: 0 }}
                      onClick={() => setSearchValue('')}
                    >
                      <CloseIcon size="small" sx={{ color: '#8493AE' }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={searchFilter}
            />
            <Button
              variant="text"
              onClick={handleFilter}
              sx={{
                p: 0,
                ':hover': {
                  bgcolor: '#ffffff',
                  color: '#000000',
                },
              }}
            >
              <FilterListIcon
                sx={{
                  fontSize: '2rem',
                  color:
                    queryParams.order_type.length !== 0 ||
                    queryParams.attributeToSort !== '' ||
                    (queryParams.endDate !== null &&
                      queryParams.endDate.getDate() !==
                        queryParams.startDate.getDate())
                      ? '#054E8B'
                      : '#8493AE',
                }}
              />
            </Button>
          </Box>
        </Box>
        {filterPopup && (
          <FilterPopup
            filterPopup={filterPopup}
            setFilterPopup={setFilterPopup}
            queryParams={queryParams}
            setQueryParams={setQueryParams}
            apply={apply}
            setApply={setApply}
          />
        )}
        <TabsCustomization
          locationCoordinates={props.locationCoordinates}
          dropDownHeight={dropDownHeight}
          setDropDownHeight={setDropDownHeight}
          apply={apply}
          setApply={setApply}
          value={tabIndex}
          route_name={user.rider_route?.name}
          searchedRoutePractices={searchedRoutePractices}
          setValue={setTabIndex}
          searchValue={searchValue || ''}
          ref={lastElementRef}
        />
        <BottomNavigationScreen />
      </Box>
    </React.Fragment>
  );
}
Logistics.propTypes = {
  locationCoordinates: PropTypes.object,
};
export default Logistics;
